import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';

const COP = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(v);

export default function Suscripcion() {
  const { user, logout, API, suscripcion, cargarSuscripcion } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Regreso desde Wompi
    if (params.get('estado') === 'ok') {
      setMsg('Pago recibido. Verificando activación...');
      setTimeout(() => {
        cargarSuscripcion();
        setMsg('Tu suscripción ha sido activada. ¡Gracias!');
      }, 2500);
    }
  }, [params, cargarSuscripcion]);

  const pagar = async (tipo) => {
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('tipo', tipo);
      const r = await axios.post(`${API}/suscripcion/checkout`, fd);
      window.location.href = r.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al generar el link de pago');
      setLoading(false);
    }
  };

  const estadoColor = {
    activa: '#16a34a',
    trial: '#2563eb',
    vencida: '#dc2626',
    cancelada: '#6b7280',
    pendiente: '#d97706',
    sin_suscripcion: '#dc2626',
  };

  const estadoLabel = {
    activa: 'Activa',
    trial: 'Prueba gratuita',
    vencida: 'Vencida',
    cancelada: 'Cancelada',
    pendiente: 'Pago en proceso',
    sin_suscripcion: 'Sin suscripción',
  };

  const esNuevo = params.get('nuevo') === '1';
  const estado = suscripcion?.estado || 'sin_suscripcion';
  const dias = suscripcion?.dias_restantes;
  const necesitaRenovar = ['vencida', 'cancelada', 'sin_suscripcion', 'pendiente'].includes(estado);
  const esPendiente = estado === 'pendiente';
  const esTrial = estado === 'trial';

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <span style={s.brand}>ConciliaApp — Suscripción</span>
        <div style={s.navRight}>
          <button onClick={() => nav('/')} style={s.btnBack}>← Volver</button>
          <button onClick={logout} style={s.btnOut}>Salir</button>
        </div>
      </nav>

      <div style={s.content}>

        {/* Estado actual */}
        <div style={s.card}>
          <h2 style={s.sectionTitle}>Estado de tu suscripción</h2>
          <div style={s.statusRow}>
            <div style={{ ...s.badge, background: estadoColor[estado] + '20', color: estadoColor[estado] }}>
              {estadoLabel[estado]}
            </div>
            {dias !== null && dias !== undefined && (
              <span style={s.dias}>
                {dias > 0 ? `${dias} días restantes` : 'Venció hoy'}
              </span>
            )}
          </div>

          {suscripcion?.empresa && (
            <p style={s.empresaNombre}>{suscripcion.empresa.nombre}</p>
          )}

          {suscripcion?.fecha_fin && (
            <p style={s.fechaInfo}>
              {necesitaRenovar ? 'Venció el' : 'Válida hasta'}{' '}
              <strong>{new Date(suscripcion.fecha_fin).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
            </p>
          )}

          {msg && <div style={s.msgOk}>{msg}</div>}
          {error && <div style={s.msgErr}>{error}</div>}
        </div>

        {/* Banner de bienvenida para nuevos registros */}
        {(esNuevo || esPendiente) && !msg && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
            <p style={{ margin: 0, fontWeight: 700, color: '#1e40af', fontSize: 15 }}>
              {esNuevo ? '¡Bienvenido a ConciliaApp!' : 'Tu cuenta está pendiente de activación'}
            </p>
            <p style={{ margin: '6px 0 0', color: '#1e3a8a', fontSize: 13 }}>
              Elige un plan para comenzar a usar la aplicación. El pago se procesa de forma segura a través de Wompi.
            </p>
          </div>
        )}

        {/* Plan disponible */}
        <h2 style={s.sectionTitle}>Plan Profesional</h2>
        <div style={s.plansRow}>

          {/* Mensual */}
          <div style={s.planCard}>
            <div style={s.planTag}>Mensual</div>
            <div style={s.planPrice}>{COP(30000)}</div>
            <div style={s.planPriceSub}>por mes</div>
            <ul style={s.features}>
              <li>Hasta 10 usuarios</li>
              <li>200 conciliaciones/mes</li>
              <li>Reportes Excel y PDF</li>
              <li>Todos los bancos colombianos</li>
              <li>Partidas conciliatorias</li>
              <li>Historial y KPIs</li>
            </ul>
            <button
              style={{ ...s.btnPay, opacity: loading ? 0.6 : 1 }}
              onClick={() => pagar('mensual')}
              disabled={loading}
            >
              {loading ? 'Redirigiendo...' : necesitaRenovar ? 'Suscribirse mensual' : 'Renovar mensual'}
            </button>
          </div>

          {/* Anual */}
          <div style={{ ...s.planCard, ...s.planCardFeatured }}>
            <div style={s.saveBadge}>Ahorra 4 meses</div>
            <div style={{ ...s.planTag, background: '#2563eb', color: '#fff' }}>Anual</div>
            <div style={{ ...s.planPrice, color: '#2563eb' }}>{COP(240000)}</div>
            <div style={s.planPriceSub}>por año · {COP(20000)}/mes</div>
            <ul style={s.features}>
              <li>Hasta 10 usuarios</li>
              <li>200 conciliaciones/mes</li>
              <li>Reportes Excel y PDF</li>
              <li>Todos los bancos colombianos</li>
              <li>Partidas conciliatorias</li>
              <li>Historial y KPIs</li>
            </ul>
            <button
              style={{ ...s.btnPay, ...s.btnPayFeatured, opacity: loading ? 0.6 : 1 }}
              onClick={() => pagar('anual')}
              disabled={loading}
            >
              {loading ? 'Redirigiendo...' : necesitaRenovar ? 'Suscribirse anual' : 'Renovar anual'}
            </button>
          </div>
        </div>

        {esTrial && (
          <div style={s.trialNote}>
            Estás en periodo de prueba gratuita. Al terminar, elige un plan para seguir usando ConciliaApp.
          </div>
        )}

        <div style={s.wompiNote}>
          Pagos procesados de forma segura por <strong>Wompi</strong>. Acepta PSE y tarjetas débito/crédito.
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui,sans-serif' },
  nav: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brand: { fontWeight: 700, fontSize: 16 },
  navRight: { display: 'flex', gap: 10 },
  btnBack: { padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'transparent', cursor: 'pointer', fontSize: 13 },
  btnOut: { padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'transparent', cursor: 'pointer', fontSize: 13 },
  content: { padding: '28px 24px', maxWidth: 860, margin: '0 auto' },
  card: { background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #e5e7eb', marginBottom: 28 },
  sectionTitle: { fontSize: 18, fontWeight: 700, margin: '0 0 16px' },
  statusRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  badge: { padding: '4px 14px', borderRadius: 20, fontWeight: 700, fontSize: 13 },
  dias: { fontSize: 13, color: '#6b7280' },
  empresaNombre: { fontSize: 15, fontWeight: 600, margin: '8px 0 4px', color: '#111' },
  fechaInfo: { fontSize: 13, color: '#6b7280', margin: 0 },
  msgOk: { marginTop: 12, padding: '10px 14px', background: '#dcfce7', color: '#16a34a', borderRadius: 8, fontSize: 13 },
  msgErr: { marginTop: 12, padding: '10px 14px', background: '#fee2e2', color: '#dc2626', borderRadius: 8, fontSize: 13 },
  plansRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 },
  planCard: { background: '#fff', borderRadius: 16, padding: 28, border: '1px solid #e5e7eb', position: 'relative' },
  planCardFeatured: { border: '2px solid #2563eb', boxShadow: '0 4px 20px rgba(37,99,235,0.12)' },
  saveBadge: { position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 14px', borderRadius: 20, whiteSpace: 'nowrap' },
  planTag: { display: 'inline-block', padding: '3px 12px', borderRadius: 20, background: '#f1f5f9', color: '#475569', fontSize: 12, fontWeight: 600, marginBottom: 12 },
  planPrice: { fontSize: 34, fontWeight: 800, color: '#111', margin: '4px 0 0' },
  planPriceSub: { fontSize: 13, color: '#6b7280', marginBottom: 20 },
  features: { listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 8 },
  btnPay: { width: '100%', padding: '13px 0', borderRadius: 10, background: '#f1f5f9', color: '#1e40af', border: '1.5px solid #cbd5e1', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  btnPayFeatured: { background: '#2563eb', color: '#fff', border: 'none' },
  trialNote: { background: '#eff6ff', color: '#1e40af', borderRadius: 10, padding: '12px 16px', fontSize: 13, marginBottom: 16 },
  wompiNote: { textAlign: 'center', fontSize: 12, color: '#9ca3af', marginTop: 8 },
};
